/**
 * ApplicationListModule (ALM)
 * 
 * Displays a grid of Forge Application cards with their related UseCase/Cadrage info.
 * Shows detailed info: status, health, app type, dates, and linked entities.
 */
import { AppWindow, Calendar, ClipboardList, Code2, FileText, Globe, Heart, Play, Server, Square, TriangleAlert, Wrench } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import forgeService from '../../services/forgeService';
import { ForgeApplicationWithContext } from '../../types';
import { Badge } from '../Common/badge';

interface ApplicationListModuleProps {
  forgeId?: string;  // Optional filter by forge ID
  showUseCaseLink?: boolean;  // Show link to related use case
  showCadrageLink?: boolean;  // Show link to related cadrage
  onApplicationClick?: (appId: string) => void;  // Custom click handler
  className?: string;
}

// Helper to capitalize first letter
const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper to get state badge variant based on status
const getStateBadgeVariant = (status?: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return 'default';
    case 'stopped':
    case 'initializing':
    case 'creating':
      return 'secondary';
    case 'error':
    case 'build_failed':
      return 'destructive';
    case 'building':
    case 'deploying':
    case 'updating':
      return 'outline';
    default:
      return 'outline';
  }
};

// Helper to get state icon based on status
const getStateIcon = (status?: string | null) => {
  if (!status) return null;
  switch (status.toLowerCase()) {
    case 'deployed':
    case 'running':
      return <Play className="h-3 w-3" />;
    case 'stopped':
      return <Square className="h-3 w-3" />;
    case 'error':
    case 'build_failed':
      return <TriangleAlert className="h-3 w-3" />;
    default:
      return null;
  }
};

// Helper to get health status color and icon
const getHealthStatusColor = (health?: string | null): string => {
  if (!health) return 'text-muted-foreground';
  switch (health.toLowerCase()) {
    case 'healthy':
      return 'text-green-600 dark:text-green-400';
    case 'unhealthy':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-muted-foreground';
  }
};

// Helper to format date
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

// Helper to format app type for display
const formatAppType = (appType?: string): string => {
  if (!appType) return '-';
  return appType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const ApplicationListModule: React.FC<ApplicationListModuleProps> = ({
  forgeId,
  showUseCaseLink = true,
  showCadrageLink = true,
  onApplicationClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState<ForgeApplicationWithContext[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchApplications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await forgeService.listApplicationsWithContext(forgeId);
        setApplications(data);
      } catch (err: any) {
        setError(err?.message || t('forge.applications.errorLoading', 'Failed to load applications'));
        console.error('Error fetching applications:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [forgeId, t]);

  // Helper to get URL from urls object
  const getFrontendUrl = (app: ForgeApplicationWithContext) => app.urls?.frontend || app.urls?.frontend_url;
  const getBackendUrl = (app: ForgeApplicationWithContext) => app.urls?.backend || app.urls?.backend_url;
  const getRepoUrl = (app: ForgeApplicationWithContext) => app.urls?.repository || app.urls?.git;

  if (isLoading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-56 bg-muted rounded-xl animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 border border-destructive/50 rounded-lg bg-destructive/10 ${className}`}>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className={`p-8 text-center border border-border rounded-xl bg-muted/30 ${className}`}>
        <AppWindow className="h-14 w-14 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground text-lg">{t('forge.applications.noApplications', 'No applications found')}</p>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
      {applications.map((app) => {
        const frontendUrl = getFrontendUrl(app);
        const backendUrl = getBackendUrl(app);
        const repoUrl = getRepoUrl(app);
        const hasLinks = frontendUrl || backendUrl || repoUrl;

        return (
          <div
            key={app.app_id}
            className="p-5 border border-border rounded-xl bg-card hover:bg-accent/50 hover:shadow-md transition-all cursor-pointer flex flex-col"
            onClick={() => onApplicationClick?.(app.app_id)}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                  <AppWindow className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground truncate" title={capitalizeFirst(app.name)}>
                    {capitalizeFirst(app.name)}
                  </h3>
                  {app.app_type && (
                    <p className="text-xs text-muted-foreground truncate" title={formatAppType(app.app_type)}>
                      {formatAppType(app.app_type)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Status Badges Row */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {app.status && (
                <Badge variant={getStateBadgeVariant(app.status)} className="flex items-center gap-1">
                  {getStateIcon(app.status)}
                  {app.status}
                </Badge>
              )}
              {app.health_status && app.health_status !== 'unknown' && (
                <span className={`flex items-center gap-1 text-xs ${getHealthStatusColor(app.health_status)}`}>
                  <Heart className="h-3.5 w-3.5" />
                  {app.health_status}
                </span>
              )}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Created Date */}
              <div className="p-2.5 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('forge.applications.created', 'Created')}</p>
                <div className="flex items-center gap-1 text-sm">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{formatDate(app.created_at)}</span>
                </div>
              </div>

              {/* Last Build Date */}
              <div className="p-2.5 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">{t('forge.applications.lastBuild', 'Last Build')}</p>
                <div className="flex items-center gap-1 text-sm">
                  <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{formatDate(app.last_build_at)}</span>
                </div>
              </div>
            </div>

            {/* External Links */}
            {hasLinks && (
              <div className="flex items-center gap-3 mb-4">
                {frontendUrl && (
                  <a
                    href={frontendUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    title={t('forge.applications.openFrontend', 'Open Frontend')}
                  >
                    <Globe className="h-4 w-4" />
                    Frontend
                  </a>
                )}
                {backendUrl && (
                  <a
                    href={backendUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    title={t('forge.applications.openBackend', 'Open Backend')}
                  >
                    <Server className="h-4 w-4" />
                    Backend
                  </a>
                )}
                {repoUrl && (
                  <a
                    href={repoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    title={t('forge.applications.openRepo', 'Open Repository')}
                  >
                    <Code2 className="h-4 w-4" />
                    Repo
                  </a>
                )}
              </div>
            )}

            {/* Etabli Links - pushed to bottom */}
            <div className="mt-auto pt-3 border-t border-border">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {showUseCaseLink && app.use_case_id ? (
                  <Link
                    to={`/cas-usage/${app.use_case_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ClipboardList className="h-4 w-4" />
                    {app.use_case_name || t('forge.applications.viewUseCase', 'View Use Case')}
                  </Link>
                ) : showUseCaseLink && (
                  <span className="text-xs text-muted-foreground italic">
                    {t('forge.applications.noLinkedUseCase', 'No linked Use Case')}
                  </span>
                )}
                
                {showCadrageLink && app.cadrage_id && (
                  <Link
                    to={`/cadrages/${app.cadrage_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    {app.cadrage_name || t('forge.applications.viewCadrage', 'View Solution')}
                  </Link>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ApplicationListModule;
